import { Link } from "react-router-dom";
import { Car, CheckCircle2, Shield, Wrench, AlertTriangle, Smartphone, ArrowRight, ArrowLeft, Star, Phone, DollarSign, Clock, Users } from "lucide-react";

const CarPage = () => {
  const benefits = [
    {
      title: "Protect Your Vehicle",
      description: "Comprehensive coverage for your car against accidents, theft, and damage.",
      icon: Car,
    },
    {
      title: "Save Up to $500/yr",
      description: "We compare rates from multiple carriers to find you the best price.",
      icon: DollarSign,
    },
    {
      title: "Liability Protection",
      description: "Coverage for damages and injuries you may cause to others.",
      icon: Shield,
    },
    {
      title: "24/7 Roadside Help",
      description: "Assistance for breakdowns, flat tires, dead batteries, and lockouts.",
      icon: Wrench,
    },
  ];

  const coverageOptions = [
    { liability: "50/100/50", deductible: "$1,000", price: "$45", popular: false },
    { liability: "100/300/100", deductible: "$500", price: "$75", popular: true },
    { liability: "250/500/100", deductible: "$500", price: "$110", popular: false },
    { liability: "250/500/250", deductible: "$250", price: "$145", popular: false },
  ];

  const discounts = [
    { name: "Safe Driver", amount: "Up to 25%" },
    { name: "Multi-Vehicle", amount: "Up to 20%" },
    { name: "Bundle & Save", amount: "Up to 15%" },
    { name: "Good Student", amount: "Up to 10%" },
  ];

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Header Banner - Blue */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Car className="w-6 h-6" />
              <div>
                <h1 className="font-bold text-lg">Auto Insurance</h1>
                <p className="text-blue-100 text-sm">Powered by My Insurance Coverage</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-600" />
              <a href="tel:201-227-6413" className="hover:text-blue-600">Need help? Call 201.227.6413</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span>My Insurance Coverage - Licensed Agent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section with Background Icon */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 relative overflow-hidden">
          {/* Background Icon */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <Car className="w-[500px] h-[500px] text-blue-900" strokeWidth={1} />
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Car className="w-4 h-4" />
                Auto Insurance
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
                Drive with Confidence.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-700">
                  Save with My Insurance Coverage.
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Get the coverage you need at a price you'll love. Compare rates from top carriers and save up to $500/year on your auto insurance.
              </p>
              <Link
                to="/quote?type=car"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-full hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30"
                data-testid="car-get-quote"
              >
                Get Your Free Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-slate-500 mt-3">Quick 5-minute application</p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl transform -rotate-2"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Quick Coverage Estimate</h3>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                  <p className="text-sm text-blue-700 mb-1">Recommended Coverage</p>
                  <p className="text-3xl font-bold text-navy-900">100/300/100</p>
                  <p className="text-sm text-slate-500 mt-1">Liability coverage</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">Starting at</p>
                    <p className="text-2xl font-bold text-navy-900">$45/mo</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">Avg. savings</p>
                    <p className="text-2xl font-bold text-emerald-600">$500/yr</p>
                  </div>
                </div>
                <Link
                  to="/quote?type=car"
                  className="block w-full py-3 bg-blue-600 text-white text-center font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  See Your Rate →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">Why Auto Insurance with Brad?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-navy-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Options */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-navy-900 mb-2 text-center">Sample Rates</h3>
          <p className="text-slate-600 text-center mb-8">Coverage options for a typical driver (clean record)</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverageOptions.map((option, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-2xl border-2 transition-all ${
                  option.popular
                    ? "border-blue-400 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {option.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <p className="text-2xl font-bold text-navy-900 mb-1">{option.liability}</p>
                <p className="text-slate-500 text-sm mb-1">Liability limits</p>
                <p className="text-slate-500 text-sm mb-3">{option.deductible} deductible</p>
                <p className="text-xl font-bold text-blue-600 mb-4">
                  {option.price}<span className="text-sm font-normal text-slate-500">/mo</span>
                </p>
                <Link
                  to="/quote?type=car"
                  className={`block w-full py-2 text-center font-semibold rounded-lg transition-colors ${
                    option.popular
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-slate-100 text-navy-700 hover:bg-slate-200"
                  }`}
                >
                  Get Quote
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-400 mt-6">
            * Actual rates may vary based on driving history, vehicle, location, and other factors.
          </p>
        </div>

        {/* Discounts Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">Available Discounts</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {discounts.map((discount, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <span className="font-medium text-navy-900">{discount.name}</span>
                <span className="text-emerald-600 font-semibold">{discount.amount}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Save on Auto Insurance?</h3>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Get your free quote in under 5 minutes. Compare rates and start saving today.
          </p>
          <Link
            to="/quote?type=car"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-700 font-bold rounded-full hover:bg-slate-100 transition-colors"
          >
            Compare Rates Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Contact Card */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-navy-900">Prefer to Speak with Bradley?</h4>
                <p className="text-sm text-slate-600">Your licensed insurance expert is ready to help</p>
              </div>
            </div>
            <a
              href="tel:201-227-6413"
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors"
            >
              Call 201.227.6413
            </a>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-slate-100 border-t border-slate-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">
            Auto insurance quotes provided through My Insurance Coverage, licensed insurance agent.
            <br />
            <span className="text-slate-400">
              Coverage underwritten by A-rated insurance carriers.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarPage;
